import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Form } from 'antd';
import { Link } from 'react-router-dom';
import AuthLayout from './auth-layout';
import {
  FormInput,
  Space,
  useLoading,
  LoadingState,
} from '@monorepo/react-components';
import { formEmailValidation } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const form = useForm<ForgotPasswordFormData>();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);
  const notificationApi = useNotification();
  const {
    dataStore: { userStore },
  } = useStore();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      updateLoadingState(LoadingState.Loading);
      await userStore.forgetPassword(data.email);

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('auth.forgotPassword.success'),
      });
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        message: t('common.error'),
        description: t('auth.forgotPassword.error'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <AuthLayout title={t('auth.forgotPassword.title')}>
      <Helmet>
        <title>Reset Password | ShortsNinja</title>
        <meta
          name="description"
          content="Forgot your ShortsNinja password? Reset it securely to regain access to your faceless videos and AI automation dashboard."
        />
        <meta
          name="keywords"
          content="forgot password, reset password, ShortsNinja, faceless videos, ai video automation, ai voiceover, automated video creation"
        />
      </Helmet>
      <Form onFinish={form.handleSubmit(onSubmit)} layout="vertical">
        <Space size={'large'} direction={'vertical'}>
          <FormInput
            placeholder={t('auth.forgotPassword.emailPlaceholder')}
            form={form}
            controllerProps={{
              name: 'email',
              rules: formEmailValidation(),
            }}
          />
          <Button
            loading={loadingState === LoadingState.Loading}
            type="primary"
            htmlType="submit"
            block
          >
            {t('auth.forgotPassword.sendButton')}
          </Button>
        </Space>
      </Form>
      <div className="auth-link">
        <Link to="/login">{t('auth.forgotPassword.backToLogin')}</Link>
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
